import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  Stepper,
  Step,
  StepLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Star as StarIcon,
  Dashboard as DashboardIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

const SubscriptionSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  useAuth(); // Verify user is authenticated
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [activeStep, setActiveStep] = useState(0);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    const processSubscription = async () => {
      if (!sessionId) {
        setError('No session ID found');
        setLoading(false);
        return;
      }

      try {
        // Verify the subscription with our backend
        await api.post('/subscription/success', { sessionId });
        
        // Fetch the updated subscription details
        const response = await api.get('/subscription/current');
        setSubscription(response.data.subscription);
        
        // Start the success animation
        const steps = [0, 1, 2];
        steps.forEach((step, index) => {
          setTimeout(() => {
            setActiveStep(step + 1);
          }, (index + 1) * 1000);
        });
        
      } catch (err) {
        console.error('Subscription processing error:', err);
        setError(err.response?.data?.message || 'Failed to process subscription');
      } finally {
        setLoading(false);
      }
    };

    processSubscription();
  }, [sessionId]);

  const steps = [
    'Payment Processed',
    'Account Upgraded',
    'Features Activated'
  ];

  const getWelcomeMessage = () => {
    if (!subscription) return 'Welcome to SoldiKeeper!';
    
    const planNames = {
      standard: 'Standard',
      premium: 'Premium',
      family: 'Family',
      business: 'Business'
    };
    
    return `Welcome to SoldiKeeper ${planNames[subscription.plan] || subscription.plan}!`;
  };

  const getFeatureList = () => {
    if (!subscription) return [];
    
    const features = {
      standard: [
        'Track up to 200 transactions per month',
        'Up to 15 budget categories',
        'Advanced reports and analytics',
        'Data export capabilities',
        'Email support'
      ],
      premium: [
        'Unlimited transactions and categories',
        'AI-powered spending insights',
        'Advanced analytics and trends',
        'Receipt scanning with OCR',
        'Custom reports and charts',
        'Priority support'
      ],
      family: [
        'All Premium features',
        'Up to 5 family members',
        'Shared budgets and goals',
        'Family spending analytics',
        'Role-based permissions'
      ],
      business: [
        'All Family features',
        'Up to 15 team members',
        'Business expense workflows',
        'Advanced business reports',
        'API access',
        'Dedicated support'
      ]
    };
    
    return features[subscription.plan] || [];
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Processing your subscription...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Alert severity="error" sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Subscription Error
          </Typography>
          {error}
        </Alert>
        <Box sx={{ textAlign: 'center' }}>
          <Button 
            variant="contained" 
            onClick={() => navigate('/api/subscription/plans')}
            sx={{ mr: 2 }}
          >
            Back to Plans
          </Button>
          <Button 
            variant="outlined" 
            onClick={() => window.open('mailto:support@soldikeeper.com')}
          >
            Contact Support
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 8 }}>
      <Card elevation={8} sx={{ borderRadius: 3 }}>
        <CardContent sx={{ p: 6 }}>
          {/* Success Header */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <CheckCircleIcon 
              sx={{ 
                fontSize: 80, 
                color: 'success.main', 
                mb: 2,
                animation: 'pulse 2s infinite'
              }} 
            />
            <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
              {getWelcomeMessage()}
            </Typography>
            <Typography variant="h6" color="text.secondary">
              Your subscription has been activated successfully
            </Typography>
          </Box>

          {/* Progress Stepper */}
          <Box sx={{ mb: 4 }}>
            <Stepper activeStep={activeStep} alternativeLabel>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>

          <Divider sx={{ my: 4 }} />

          {/* Subscription Details */}
          {subscription && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <StarIcon sx={{ mr: 1, color: 'primary.main' }} />
                Your {subscription.planDetails?.name} Plan Features
              </Typography>
              
              <List>
                {getFeatureList().map((feature, index) => (
                  <ListItem key={index} sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />
                    </ListItemIcon>
                    <ListItemText primary={feature} />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          <Divider sx={{ my: 4 }} />

          {/* Next Steps */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom>
              Get Started
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              Here's what you can do next to make the most of your subscription:
            </Typography>
            
            <List>
              <ListItem>
                <ListItemIcon>
                  <DashboardIcon color="primary" />
                </ListItemIcon>
                <ListItemText 
                  primary="Explore Your Dashboard" 
                  secondary="Start tracking your expenses and setting up budgets"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <SettingsIcon color="primary" />
                </ListItemIcon>
                <ListItemText 
                  primary="Customize Your Categories" 
                  secondary="Set up budget categories that match your spending habits"
                />
              </ListItem>
              {subscription?.plan !== 'standard' && (
                <ListItem>
                  <ListItemIcon>
                    <StarIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Try AI Insights" 
                    secondary="Get personalized spending insights and recommendations"
                  />
                </ListItem>
              )}
            </List>
          </Box>

          {/* Action Buttons */}
          <Box sx={{ textAlign: 'center', pt: 2 }}>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/dashboard')}
              startIcon={<DashboardIcon />}
              sx={{ mr: 2, py: 1.5, px: 4 }}
            >
              Go to Dashboard
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate('/dashboard/settings')}
              startIcon={<SettingsIcon />}
              sx={{ py: 1.5, px: 4 }}
            >
              Account Settings
            </Button>
          </Box>

          {/* Support Note */}
          <Box sx={{ textAlign: 'center', mt: 4, pt: 2, borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="body2" color="text.secondary">
              Need help getting started? 
              <Button 
                variant="text" 
                size="small" 
                onClick={() => window.open('mailto:support@soldikeeper.com')}
                sx={{ ml: 1 }}
              >
                Contact our support team
              </Button>
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};

export default SubscriptionSuccess;
